import React, { useState } from 'react';
import { KBBlock } from '../../../types';
import { renderParagraph } from '../utils/renderInline';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const ToggleBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const [open, setOpen] = useState(block.data?.isOpen ?? false);
  const title = block.content;
  const body = block.data?.body ?? '';

  if (isEditing) {
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 border-b border-gray-200">
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <input
            autoFocus
            type="text"
            value={title}
            placeholder="Toggle title…"
            onChange={(e) => onChange({ content: e.target.value })}
            className="flex-1 bg-transparent border-none outline-none text-sm font-medium text-gray-700"
          />
        </div>
        <div className="px-4 py-3">
          <textarea
            value={body}
            placeholder="Toggle content (shown when expanded)…"
            onChange={(e) => onChange({ data: { ...block.data, body: e.target.value } })}
            onKeyDown={(e) => { if (e.key === 'Escape') onFinish(); }}
            rows={3}
            className="w-full bg-transparent border-none outline-none text-sm text-gray-600 resize-none"
          />
        </div>
        <div className="px-4 pb-3">
          <button onClick={onFinish} className="text-xs bg-blue-600 text-white px-3 py-1 rounded">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 bg-gray-50 hover:bg-gray-100 px-4 py-3 transition-colors text-left"
      >
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-sm font-medium text-gray-700">
          {title || <em className="text-gray-300 font-normal">Toggle title</em>}
        </span>
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-gray-100">
          <div className="text-sm text-gray-600 leading-relaxed">
            {body ? renderParagraph(body) : <em className="text-gray-300">Empty</em>}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToggleBlock;
